"use client";

import { useState, useMemo, useEffect, useRef, Suspense } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Eye,
  History,
  Loader2,
} from "lucide-react";
import { BrandLogo } from "@/components/brand";
import { useT } from "@/i18n/provider";
import type { T } from "@/i18n/translate";
import { EnglishMarketingScope } from "@/components/english-scope";

type Intent = "default" | "codebase" | "issues";
type Plan = "starter" | "growth" | "pro" | null;

function planLabels(t: T): Record<Exclude<Plan, null>, { name: string; price: string }> {
  return {
    starter: { name: t("signup.plans.starter"), price: t("signup.plans.starterPrice") },
    growth: { name: t("signup.plans.growth"), price: t("signup.plans.growthPrice") },
    pro: { name: t("signup.plans.pro"), price: t("signup.plans.proPrice") },
  };
}

export default function SignupPage() {
  return (
    <EnglishMarketingScope>
      <Suspense fallback={null}>
        <SignupInner />
      </Suspense>
    </EnglishMarketingScope>
  );
}

function SignupInner() {
  const t = useT();
  const PLAN_LABELS = planLabels(t);
  const params = useSearchParams();
  const rawIntent = params?.get("intent") ?? "default";
  const intent: Intent =
    rawIntent === "codebase" || rawIntent === "issues" ? rawIntent : "default";

  const rawPlan = params?.get("plan");
  const plan: Plan =
    rawPlan === "starter" || rawPlan === "growth" || rawPlan === "pro"
      ? rawPlan
      : null;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [loadDemoData, setLoadDemoData] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Signup is email-verified: submit no longer logs you straight in. The form
  // moves through form → verify (enter the emailed code) → done/review.
  const [step, setStep] = useState<"form" | "verify" | "review">("form");
  const [code, setCode] = useState("");
  const [info, setInfo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  // Honeypot: a real human never fills this hidden field. Bots that auto-fill
  // every input trip it and get silently routed to review.
  const [website, setWebsite] = useState("");
  // Min-submit-time anchor — stamped once on mount (not during render, which
  // would be an impure Date.now() call).
  const formRenderedAt = useRef<number>(0);
  useEffect(() => {
    formRenderedAt.current = Date.now();
  }, []);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const headline = useMemo(() => {
    if (plan) return t("signup.headlinePlan", { plan: PLAN_LABELS[plan].name });
    if (intent === "codebase") return t("signup.headlineCodebase");
    if (intent === "issues") return t("signup.headlineIssues");
    return t("signup.headlineDefault");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, plan, t]);

  const subline = useMemo(() => {
    if (plan) return t("signup.sublinePlan");
    if (intent === "codebase") return t("signup.sublineCodebase");
    if (intent === "issues") return t("signup.sublineIssues");
    return t("signup.sublineDefault");
  }, [intent, plan, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          password,
          company,
          loadDemoData,
          plan: plan ?? undefined,
          source: intent,
          website, // honeypot
          formRenderedAt: formRenderedAt.current,
        }),
      });
      const data = await res.json().catch(() => ({}) as any);
      if (!res.ok || !data.ok) {
        setError(data.error ?? t("signup.genericError"));
        setBusy(false);
        return;
      }
      setBusy(false);
      if (data.status === "review" || data.status === "waitlisted") {
        setStep("review");
        return;
      }
      // verification_sent → move to the code-entry step.
      setStep("verify");
      setCooldown(60);
      setInfo(t("signup.checkEmail"));
    } catch {
      setError(t("signup.networkError"));
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/auth/signup/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json().catch(() => ({}) as any);
      if (!res.ok || !data.ok) {
        setError(data.error ?? t("signup.codeInvalid"));
        setBusy(false);
        return;
      }
      if (data.status === "pending_review") {
        setStep("review");
        setBusy(false);
        return;
      }
      // activated → hard navigation so middleware sees the new session cookie.
      setInfo(t("signup.verifiedBody"));
      window.location.href = data.redirectTo || "/app";
    } catch {
      setError(t("signup.networkError"));
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setError(null);
    setInfo(null);
    try {
      const res = await fetch("/api/auth/signup/resend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}) as any);
      if (!res.ok) {
        setError(data.error ?? t("signup.networkError"));
        if (res.status === 429) setCooldown(60);
        return;
      }
      setInfo(t("signup.resendSent"));
      setCooldown(60);
    } catch {
      setError(t("signup.networkError"));
    }
  };

  const inputCls =
    "w-full rounded-lg border border-line-subtle bg-bg-base px-4 py-2.5 text-[14px] text-fg placeholder:text-fg-muted focus:outline-none focus:border-brand-400/50 focus:ring-2 focus:ring-brand-500/20 transition";

  return (
    <div className="min-h-screen bg-bg-base grid-bg">
      <header className="border-b border-line-subtle bg-bg-base/80 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-6 h-14 flex items-center justify-between">
          <Link href="/" className="flex items-center">
            <BrandLogo size={20} wordmarkClassName="text-[15px]" />
          </Link>
          <Link
            href="/login"
            className="text-[13px] text-fg-dim hover:text-fg-bright transition"
          >
            {t("signup.alreadyHaveAccount")}{" "}
            <span className="text-brand-300">{t("signup.signIn")}</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left: pitch rail */}
          <aside className="lg:col-span-2 space-y-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-brand-300 font-medium">
                {plan
                  ? t("signup.eyebrowPlan", { plan: PLAN_LABELS[plan].name })
                  : intent === "codebase"
                    ? t("signup.eyebrowCodebase")
                    : intent === "issues"
                      ? t("signup.eyebrowIssues")
                      : t("signup.eyebrowDefault")}
              </div>
              <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-fg-bright leading-tight">
                {headline}
              </h1>
              <p className="mt-4 text-[14.5px] text-fg-dim leading-relaxed">
                {subline}
              </p>
            </div>

            {plan && (
              <div className="card p-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-fg-muted">
                    {t("signup.selectedPlan")}
                  </div>
                  <div className="mt-1 text-[14px] font-semibold text-fg-bright">
                    {PLAN_LABELS[plan].name}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[14px] font-semibold text-fg-bright tabular-nums">
                    {PLAN_LABELS[plan].price}
                  </div>
                  <Link
                    href="/#pricing"
                    className="text-[11px] text-brand-300 hover:text-brand-200 transition"
                  >
                    {t("signup.changePlan")}
                  </Link>
                </div>
              </div>
            )}

            <ul className="space-y-3">
              <PerkRow
                icon={<Eye size={14} className="text-brand-300" />}
                title={t("signup.perkIsolatedTitle")}
                body={t("signup.perkIsolatedBody")}
              />
              <PerkRow
                icon={<ShieldCheck size={14} className="text-brand-300" />}
                title={t("signup.perkApprovalTitle")}
                body={t("signup.perkApprovalBody")}
              />
              <PerkRow
                icon={<History size={14} className="text-brand-300" />}
                title={t("signup.perkAuditTitle")}
                body={t("signup.perkAuditBody")}
              />
            </ul>

            <div className="card p-4 flex items-start gap-3">
              <CheckCircle2
                size={16}
                className="text-status-low shrink-0 mt-0.5"
              />
              <div className="text-[12.5px] text-fg-dim leading-relaxed">
                {t("signup.freeBlurb")}
              </div>
            </div>
          </aside>

          {/* Right: form / verify / review */}
          <div className="lg:col-span-3">
            {step === "review" && (
              <div className="card p-6 md:p-8 space-y-3">
                <div className="text-[16px] font-semibold text-fg-bright">
                  {t("signup.reviewTitle")}
                </div>
                <p className="text-[13.5px] text-fg-dim leading-relaxed">
                  {t("signup.reviewBody")}
                </p>
              </div>
            )}

            {step === "verify" && (
              <form onSubmit={verify} className="card p-6 md:p-8 space-y-5">
                <div>
                  <div className="text-[16px] font-semibold text-fg-bright">
                    {t("signup.verifyTitle")}
                  </div>
                  <p className="mt-1.5 text-[13px] text-fg-dim leading-relaxed">
                    {t("signup.verifySubtitle", { email })}
                  </p>
                </div>

                <Field label={t("signup.codeLabel")} required>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    className={`${inputCls} tracking-[0.4em] text-center text-[18px]`}
                    autoFocus
                    required
                  />
                </Field>

                {info && (
                  <div className="rounded-lg bg-status-low/10 border border-status-low/20 px-3 py-2 text-[12px] text-status-low">
                    {info}
                  </div>
                )}
                {error && (
                  <div className="rounded-lg bg-status-critical/10 border border-status-critical/20 px-3 py-2 text-[12px] text-status-critical">
                    {error}
                  </div>
                )}

                <div className="flex items-center justify-between gap-4 pt-1">
                  <button
                    type="button"
                    onClick={resend}
                    disabled={cooldown > 0}
                    className="text-[12.5px] text-brand-300 hover:text-brand-200 transition disabled:text-fg-muted disabled:cursor-not-allowed"
                  >
                    {cooldown > 0 ? t("signup.resendIn", { sec: cooldown }) : t("signup.resend")}
                  </button>
                  <button
                    type="submit"
                    disabled={busy || code.length !== 6}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-brand-grad text-white text-[14px] font-medium shadow-glow hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {busy ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        {t("signup.verifying")}
                      </>
                    ) : (
                      <>
                        {t("signup.verifyCta")}
                        <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setStep("form");
                    setCode("");
                    setError(null);
                    setInfo(null);
                  }}
                  className="text-[12px] text-fg-muted hover:text-fg-dim transition"
                >
                  {t("signup.useDifferentEmail")}
                </button>
              </form>
            )}

            {step === "form" && (
            <form onSubmit={submit} className="card p-6 md:p-8 space-y-5">
              {/* Honeypot — visually hidden, off-tab, never autofilled by a human. */}
              <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden">
                <label htmlFor="website-url">Website</label>
                <input
                  id="website-url"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("signup.fullName")} required>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("signup.fullNamePlaceholder")}
                    className={inputCls}
                    autoFocus
                    required
                  />
                </Field>
                <Field label={t("signup.workEmail")} required>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("signup.workEmailPlaceholder")}
                    className={inputCls}
                    autoComplete="email"
                    required
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("signup.company")} required>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder={t("signup.companyPlaceholder")}
                    className={inputCls}
                    required
                  />
                </Field>
                <Field label={t("signup.password")} required>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("signup.passwordPlaceholder")}
                    className={inputCls}
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </Field>
              </div>

              <label className="flex items-start gap-3 rounded-lg border border-line-subtle bg-bg-base/40 px-3 py-2.5 cursor-pointer hover:border-brand-400/40 transition">
                <input
                  type="checkbox"
                  checked={loadDemoData}
                  onChange={(e) => setLoadDemoData(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-brand-500"
                />
                <span className="text-[12.5px] text-fg-dim leading-relaxed">
                  <span className="block text-[13px] text-fg-bright font-medium">
                    {t("signup.loadDemoTitle")}
                  </span>
                  {t("signup.loadDemoBody")}
                </span>
              </label>

              {error && (
                <div className="rounded-lg bg-status-critical/10 border border-status-critical/20 px-3 py-2 text-[12px] text-status-critical">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between gap-4 pt-1">
                <p className="text-[11px] text-fg-muted max-w-xs leading-relaxed">
                  {t("signup.fairUseNote")}
                </p>
                <button
                  type="submit"
                  disabled={
                    busy || !name || !email || !company || password.length < 8
                  }
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-brand-grad text-white text-[14px] font-medium shadow-glow hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {busy ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      {t("signup.creatingWorkspace")}
                    </>
                  ) : (
                    <>
                      {t("signup.createWorkspace")}
                      <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[11px] text-fg-muted mb-1.5 block">
        {label}
        {required && <span className="text-brand-300 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function PerkRow({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <div className="h-7 w-7 rounded-lg bg-brand-500/15 border border-brand-500/25 grid place-items-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[13.5px] font-medium text-fg-bright">{title}</div>
        <div className="text-[12.5px] text-fg-dim leading-relaxed mt-0.5">
          {body}
        </div>
      </div>
    </li>
  );
}
